import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import { API_ENDPOINTS } from '../../config/api';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface Transaction {
  id: string;
  type: 'contribution' | 'withdrawal' | 'loan' | 'welfare' | 'merry-go-round' | 'transfer';
  amount: number;
  description: string;
  date: string | null;
  status: 'completed' | 'pending' | 'failed';
  memberName?: string;
  memberAvatar?: string;
  reference?: string;
  created_at?: string;
  updated_at?: string;
  timestamp?: string;
  transaction_date?: string;
}

const ChamaTransactions: React.FC = () => {
  const { chamaId } = useParams<{ chamaId: string }>();
  const colors = getThemeColors('dark');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (chamaId) {
      loadTransactions();
    }
  }, [chamaId]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const authToken = localStorage.getItem('authToken');

      if (!authToken) {
        console.error('❌ ChamaTransactions: No auth token found');
        return;
      }

      const response = await fetch(API_ENDPOINTS.CHAMA_TRANSACTIONS(chamaId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setTransactions(data.data);
        } else {
          setTransactions([]);
        }
      } else {
        console.error('❌ ChamaTransactions: Failed to load transactions');
        setTransactions([]);
      }
    } catch (error) {
      console.error('❌ ChamaTransactions: Error loading transactions:', error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'contribution': return '💰';
      case 'withdrawal': return '📉';
      case 'loan': return '💳';
      case 'welfare': return '❤️';
      case 'merry-go-round': return '🔄';
      case 'transfer': return '↗️';
      default: return '💳';
    }
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'contribution': return colors.success;
      case 'withdrawal': return colors.error;
      case 'loan': return colors.primary;
      case 'welfare': return colors.warning;
      case 'merry-go-round': return colors.info;
      case 'transfer': return colors.secondary;
      default: return colors.textSecondary;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return colors.success;
      case 'pending': return colors.warning;
      case 'failed': return colors.error;
      default: return colors.textSecondary;
    }
  };

  const filteredTransactions = transactions.filter(transaction => {
    const matchesFilter = activeFilter === 'all' || transaction.type === activeFilter;
    const matchesSearch = searchTerm === '' ||
      transaction.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.memberName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.reference?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  const renderTransactionCard = (transaction: Transaction) => (
    <div key={transaction.id} style={{ ...styles.transactionCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
      <div style={styles.transactionHeader}>
        <div style={styles.transactionIcon}>
          <span style={{ fontSize: '24px' }}>{getTransactionIcon(transaction.type)}</span>
        </div>
        <div style={styles.transactionInfo}>
          <div style={{ ...styles.transactionType, color: colors.text }}>
            {transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1).replace('-', ' ')}
          </div>
          <div style={{ ...styles.transactionDate, color: colors.textSecondary }}>
            {(() => {
              // Try multiple date fields that might be returned by the backend
              const dateToUse = transaction.created_at || transaction.date || transaction.timestamp || transaction.transaction_date;
              if (dateToUse) {
                try {
                  const date = new Date(dateToUse);
                  if (!isNaN(date.getTime())) {
                    return `${date.toLocaleDateString()} • ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                  }
                } catch (error) {
                  console.warn('Invalid date format:', dateToUse);
                }
              }
              // Fallback: show current date/time as placeholder for demo
              const now = new Date();
              return `${now.toLocaleDateString()} • ${now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
            })()}
          </div>
        </div>
        <div style={{ ...styles.transactionStatus, backgroundColor: getStatusColor(transaction.status) + '20', border: `1px solid ${getStatusColor(transaction.status)}` }}>
          <span style={{ ...styles.statusText, color: getStatusColor(transaction.status) }}>
            {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
          </span>
        </div>
      </div>

      <div style={styles.transactionDetails}>
        <div style={styles.transactionAmount}>
          <div style={{ ...styles.amountLabel, color: colors.textSecondary }}>Amount</div>
          <div style={{ ...styles.amountValue, color: getTransactionColor(transaction.type) }}>
            {transaction.type === 'withdrawal' ? '-' : '+'}{formatCurrency(transaction.amount)}
          </div>
        </div>

        <div style={styles.transactionDescription}>
          <div style={{ ...styles.descriptionLabel, color: colors.textSecondary }}>Description</div>
          <div style={{ ...styles.descriptionValue, color: colors.text }}>{transaction.description}</div>
        </div>

        {transaction.memberName && (
          <div style={styles.transactionMember}>
            <div style={{ ...styles.memberLabel, color: colors.textSecondary }}>Member</div>
            <div style={styles.memberInfo}>
              {transaction.memberAvatar && (
                <img src={transaction.memberAvatar} alt={transaction.memberName} style={styles.memberAvatar} />
              )}
              <div style={{ ...styles.memberName, color: colors.text }}>{transaction.memberName}</div>
            </div>
          </div>
        )}

        {transaction.reference && (
          <div style={styles.transactionReference}>
            <div style={{ ...styles.referenceLabel, color: colors.textSecondary }}>Reference</div>
            <div style={{ ...styles.referenceValue, color: colors.text, fontFamily: 'monospace' }}>{transaction.reference}</div>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div>Loading transactions...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Chama Transactions"
        showBackButton={true}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Summary Cards */}
          <div style={styles.summaryGrid}>
            <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.summaryIcon}>📊</div>
              <div style={styles.summaryInfo}>
                <div style={{ ...styles.summaryValue, color: colors.text }}>
                  {transactions.length}
                </div>
                <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Total Transactions</div>
              </div>
            </div>

            <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.summaryIcon}>💰</div>
              <div style={styles.summaryInfo}>
                <div style={{ ...styles.summaryValue, color: colors.text }}>
                  {formatCurrency(transactions.reduce((sum, t) => sum + t.amount, 0))}
                </div>
                <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Total Amount</div>
              </div>
            </div>

            <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.summaryIcon}>✅</div>
              <div style={styles.summaryInfo}>
                <div style={{ ...styles.summaryValue, color: colors.text }}>
                  {transactions.filter(t => t.status === 'completed').length}
                </div>
                <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Completed</div>
              </div>
            </div>

            <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.summaryIcon}>⏳</div>
              <div style={styles.summaryInfo}>
                <div style={{ ...styles.summaryValue, color: colors.text }}>
                  {transactions.filter(t => t.status === 'pending').length}
                </div>
                <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Pending</div>
              </div>
            </div>
          </div>

          {/* Search and Filter */}
          <div style={styles.controlsContainer}>
            <div style={styles.searchContainer}>
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ ...styles.searchInput, backgroundColor: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }}
              />
            </div>

            <div style={styles.filterContainer}>
              {[
                { id: 'all', label: 'All', icon: '📱' },
                { id: 'contribution', label: 'Contributions', icon: '💰' },
                { id: 'withdrawal', label: 'Withdrawals', icon: '📉' },
                { id: 'loan', label: 'Loans', icon: '💳' },
                { id: 'welfare', label: 'Welfare', icon: '❤️' },
                { id: 'merry-go-round', label: 'Merry-Go-Round', icon: '🔄' },
              ].map(filter => (
                <button
                  key={filter.id}
                  onClick={() => setActiveFilter(filter.id)}
                  style={{
                    ...styles.filterButton,
                    backgroundColor: activeFilter === filter.id ? colors.primary : 'transparent',
                    color: activeFilter === filter.id ? 'white' : colors.text,
                  }}
                >
                  <span style={{ marginRight: '4px' }}>{filter.icon}</span>
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          {/* Transactions List */}
          <div style={styles.transactionsList}>
            {filteredTransactions.length > 0 ? (
              filteredTransactions.map(renderTransactionCard)
            ) : (
              <div style={{ ...styles.emptyState, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
                <div style={styles.emptyIcon}>📊</div>
                <div style={{ ...styles.emptyTitle, color: colors.text }}>No transactions found</div>
                <div style={{ ...styles.emptySubtitle, color: colors.textSecondary }}>
                  {searchTerm
                    ? `No transactions match your search "${searchTerm}".`
                    : activeFilter === 'all'
                      ? 'No transactions have been recorded yet.'
                      : `No ${activeFilter} transactions found.`
                  }
                </div>
              </div>
            )}
          </div>

          {/* Export Button */}
          {filteredTransactions.length > 0 && (
            <button style={{ ...styles.exportButton, backgroundColor: colors.primary }}>
              Export Transactions
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--background-color)',
    paddingBottom: 80,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: 80,
    paddingBottom: 80,
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: spacing.md,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxxl,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: spacing.md,
    marginBottom: spacing.lg,
  },
  summaryCard: {
    borderRadius: borderRadius.lg,
    padding: spacing.md,
    display: 'flex',
    alignItems: 'center',
    ...getShadowStyle('sm'),
  },
  summaryIcon: {
    fontSize: '24px',
    marginRight: spacing.md,
  },
  summaryInfo: {
    flex: 1,
  },
  summaryValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
  },
  summaryLabel: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  controlsContainer: {
    marginBottom: spacing.lg,
  },
  searchContainer: {
    marginBottom: spacing.md,
  },
  searchInput: {
    width: '100%',
    padding: spacing.md,
    borderRadius: borderRadius.md,
    fontSize: typography.fontSize.base,
    outline: 'none',
  },
  filterContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  filterButton: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderRadius: borderRadius.md,
    border: 'none',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  transactionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
  },
  transactionCard: {
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    ...getShadowStyle('sm'),
  },
  transactionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  transactionIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(33, 150, 243, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  transactionInfo: {
    flex: 1,
  },
  transactionType: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
  transactionDate: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  transactionStatus: {
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.md,
  },
  statusText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
  },
  transactionDetails: {
    // Container for transaction details
  },
  transactionAmount: {
    marginBottom: spacing.md,
  },
  amountLabel: {
    fontSize: typography.fontSize.sm,
  },
  amountValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    marginTop: spacing.xs / 2,
  },
  transactionDescription: {
    marginBottom: spacing.md,
  },
  descriptionLabel: {
    fontSize: typography.fontSize.sm,
  },
  descriptionValue: {
    fontSize: typography.fontSize.base,
    marginTop: spacing.xs / 2,
  },
  transactionMember: {
    marginBottom: spacing.md,
  },
  memberLabel: {
    fontSize: typography.fontSize.sm,
  },
  memberInfo: {
    display: 'flex',
    alignItems: 'center',
    marginTop: spacing.xs / 2,
  },
  memberAvatar: {
    width: 24,
    height: 24,
    borderRadius: 12,
    marginRight: spacing.sm,
  },
  memberName: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
  transactionReference: {
    marginBottom: spacing.md,
  },
  referenceLabel: {
    fontSize: typography.fontSize.sm,
  },
  referenceValue: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  emptyState: {
    textAlign: 'center',
    padding: spacing.xl,
    borderRadius: borderRadius.lg,
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: spacing.md,
  },
  emptyTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.sm,
  },
  emptySubtitle: {
    fontSize: typography.fontSize.base,
  },
  exportButton: {
    width: '100%',
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    border: 'none',
    color: 'white',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
    marginTop: spacing.lg,
  },
};

export default ChamaTransactions;